import React, { useState } from 'react';
import { UserPlus, UserMinus, Clock, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

/**
 * FollowButton Component
 * 
 * Handles follow/unfollow actions with support for public and private accounts.
 * 
 * States:
 * - not_following: Show "Follow" button
 * - pending: Show "Requested" button (for private accounts awaiting approval)
 * - following: Show "Following" button (click to unfollow)
 */
function FollowButton({ 
  userId, 
  isPrivate = false, 
  size = 'md', 
  showLabel = true,
  className = '' 
}) {
  const { user } = useAuth();
  const { 
    getFollowStatus, 
    followUser, 
    unfollowUser, 
    cancelFollowRequest 
  } = useApp();
  
  const [loading, setLoading] = useState(false);
  const [showUnfollow, setShowUnfollow] = useState(false);

  // Don't show button for own profile or if not logged in
  if (!user || userId === user.id) {
    return null;
  }

  const status = getFollowStatus(userId);

  const handleClick = async () => {
    setLoading(true);
    try {
      if (status === 'following') {
        await unfollowUser(userId);
      } else if (status === 'pending') {
        await cancelFollowRequest(userId);
      } else {
        await followUser(userId);
      }
    } catch (error) {
      console.error('Follow action failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const iconSize = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  // Render based on status
  if (status === 'following') {
    return (
      <button
        onClick={handleClick}
        onMouseEnter={() => setShowUnfollow(true)}
        onMouseLeave={() => setShowUnfollow(false)}
        disabled={loading}
        className={`flex items-center gap-1.5 rounded-lg font-medium transition-all ${sizeClasses[size]} ${
          showUnfollow
            ? 'bg-red-500/20 border border-red-500 text-red-400 hover:bg-red-500/30'
            : 'bg-dive-neon/20 border border-dive-neon text-dive-neon'
        } ${className}`}
      >
        {loading ? (
          <Loader2 className={`${iconSize[size]} animate-spin`} />
        ) : showUnfollow ? (
          <UserMinus className={iconSize[size]} />
        ) : null}
        {showLabel && (showUnfollow ? 'Unfollow' : 'Following')}
      </button>
    );
  }

  if (status === 'pending') {
    return (
      <button
        onClick={handleClick}
        disabled={loading}
        className={`flex items-center gap-1.5 rounded-lg font-medium transition-all ${sizeClasses[size]} bg-dive-gray border border-dive-gray text-gray-400 hover:border-red-500 hover:text-red-400 ${className}`}
      >
        {loading ? (
          <Loader2 className={`${iconSize[size]} animate-spin`} />
        ) : (
          <Clock className={iconSize[size]} />
        )}
        {showLabel && 'Requested'}
      </button>
    );
  }

  // Not following - show Follow button
  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={`flex items-center gap-1.5 rounded-lg font-medium transition-all ${sizeClasses[size]} bg-dive-neon text-white hover:bg-dive-neon/80 ${className}`}
    >
      {loading ? (
        <Loader2 className={`${iconSize[size]} animate-spin`} />
      ) : (
        <UserPlus className={iconSize[size]} />
      )}
      {showLabel && 'Follow'}
    </button>
  );
}

export default FollowButton;

